import { evolve, omit, piped } from 'remeda';

import {
    type Article,
    type DetailedRevision,
    type DetailedUnityRevision,
    isArticle,
    type NewArticle,
    type UnityArticle,
} from '@@api/services/content/schemas/article';
import deserializeState from '@@plate/serialization/unity/utils/deserializeState';
import serializeState from '@@plate/serialization/unity/utils/serializeState';
import unwrapSerializedState from '@@plate/serialization/unity/utils/unwrapSerializedState';
import wrapSerializedState from '@@plate/serialization/unity/utils/wrapSerializedState';

import {
    getVariantsFieldsFromContent,
    removeUnwantedElements,
    transformToArticleVariants,
} from './utils/deserialization';
import { transformToArticleContent } from './utils/serialization';

const transformVariants = piped(getVariantsFieldsFromContent, transformToArticleVariants);

const contentTransformations = piped(
    unwrapSerializedState,
    removeUnwantedElements,
    wrapSerializedState,
    deserializeState,
);

export const deserializeArticle = (entity: UnityArticle): Article =>
    // If the article is a Unity article, we need to get the variants out of the content
    entity.contentOwner === 'UNITY'
        ? evolve(
              { ...entity, variants: transformVariants(entity) },
              {
                  content: contentTransformations,
              },
          )
        : // Else we just transform the content
          evolve(
              // Even though `variants` is not used in non-Unity articles, we add it for type simplicity
              { ...entity, variants: [] },
              {
                  content: deserializeState,
              },
          );

export const serializeArticle = (entity: Article | NewArticle): UnityArticle | NewArticle => {
    // When a article is cloned, we will have no data in the body except metadata. So only convert
    // the content if it already exists on the entity. We need create a futureproof solution here...
    if (isArticle(entity)) {
        const newContent = transformToArticleContent(entity);

        return evolve(omit(entity, ['variants']), {
            content: () => serializeState(newContent),
        });
    }

    return entity;
};

export const deserializeDetailedRevision = (entity: DetailedUnityRevision): DetailedRevision => {
    const revisionData = {
        ...entity.revisionData,
        variants: transformVariants(entity.revisionData),
    };

    const deserializedRevisionData = evolve(revisionData, {
        content: contentTransformations,
    });

    return {
        ...entity,
        revisionData: deserializedRevisionData,
    };
};
